'use client';

import React from 'react';
import clsx from 'clsx';
import { LucideChevronLeft } from 'lucide-react';

import { StepsIndicator } from '@/components/step-indicator/StepsIndicator';
import type { UseMultistepFormStateResult } from '@/sections/forms/multistep-form/hooks/useMultistepFormState';
import { useTranslations } from '@/lib/locale-context';

type FooterProps = {
  formState: UseMultistepFormStateResult;
  variant?: string | null;
};

const Footer: React.FC<FooterProps> = ({ formState, variant }) => {
  const { t } = useTranslations();
  const isStepPreset =
    Boolean(formState.presetCallbackRequestNotes) ||
    Boolean(formState.presetWebsiteSelectedTopics);
  const showBackButton = !isStepPreset && formState.canGoBack;
  const isDarkVariant = variant === 'dark';
  const nextLabel =
    formState.currentStep === formState.totalSteps
      ? t('gatewayForms.submit')
      : t('gatewayForms.next');

  if (!formState.showFooter) return null;

  const footerWrapperClasses = clsx(
    "relative w-full after:absolute after:bottom-0 after:left-1/2 after:h-px after:w-screen after:-translate-x-1/2 after:bg-neutral-200 after:content-[''] xl:absolute xl:bottom-0 xl:left-0 xl:right-0 xl:after:hidden",
    isDarkVariant && 'my-4 mb-0 xl:my-12',
  );
  const rightPanelClasses = clsx(
    'bg-white xl:absolute xl:inset-y-0 xl:right-0 xl:w-1/2',
    isDarkVariant && 'xl:rounded-xl',
  );
  const dividerClasses = clsx(
    'xl:absolute xl:top-0 xl:z-10 xl:h-px xl:bg-gray-200',
    isDarkVariant
      ? 'xl:right-0 xl:w-1/2'
      : 'xl:left-1/2 xl:w-screen xl:bg-[linear-gradient(to_right,transparent_0_50%,#E5E7EB_50%_100%)]',
  );
  const contentClasses = clsx(
    'mx-auto flex w-full items-center pb-0 pt-2 xl:max-w-[760px] xl:py-2',
    showBackButton ? 'justify-between' : 'justify-end',
  );
  const rightColumnClasses = clsx(
    'xl:pl-12',
    isDarkVariant ? 'xl:pr-12' : 'xl:pr-1',
  );

  return (
    <div className={footerWrapperClasses}>
      <div className={rightPanelClasses} />
      <div className={dividerClasses} />
      <div className="relative z-10 mx-auto px-4 pb-4 pt-6 sm:px-6 lg:px-8 lg:py-6 xl:px-0">
        {/* Mobile indicator */}
        {!isStepPreset && (
          <div className="mb-5 border-b border-neutral-200 pb-5 xl:hidden">
            <StepsIndicator
              totalSteps={formState.totalSteps}
              currentStep={formState.stepIndicatorCurrent}
              variant="dark"
              type="progress"
              align="left"
              showLabel
              renderLabel={({ current, total }) => (
                <span className="text-sm text-slate-500">
                  {t('progressiveForm.step', { current, total })}
                </span>
              )}
            />
          </div>
        )}

        <div className="xl:grid xl:grid-cols-2">
          <div className="hidden xl:block" />
          <div className={rightColumnClasses}>
            {/* Buttons */}
            <div className={contentClasses}>
              {showBackButton && (
                <button
                  type="button"
                  onClick={formState.onBack}
                  className={clsx(
                    'flex items-center justify-center rounded-lg border',
                    'h-10 w-10 xl:h-11 xl:w-11',
                    'border-brand-300 text-slate-900 hover:bg-neutral-50',
                  )}
                  aria-label={t('gatewayForms.back')}
                >
                  <LucideChevronLeft />
                </button>
              )}

              <button
                type="button"
                disabled={!formState.canGoNext}
                onClick={formState.onNext}
                className={clsx(
                  'flex items-center justify-center rounded-lg font-semibold',
                  'h-10 w-[132px] px-4 text-sm',
                  'sm:h-11 sm:w-[158px] sm:px-[18px] sm:text-base',
                  'gap-2 sm:gap-[10px]',
                  formState.canGoNext
                    ? 'bg-brand-700 text-white hover:bg-brand-800'
                    : 'cursor-not-allowed bg-neutral-200 text-neutral-500',
                )}
              >
                {nextLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
